// src/features/tidsregistering/pages/PeriodClosingPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Stepper, Step, StepLabel,
} from '@mui/material';
import {
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
  FileDownload as ExportIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';

import { useClosingPeriods } from '../hooks/useClosingPeriods';
import { useStaff } from '../../staff/hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useTidsregistreringClosingPeriodPermissions } from '../../auth/hooks/usePermissions';
import StaffMember from '../../../components/common/StaffMember';
import { dayOverviewsAPI } from '../services/dayOverviews';
import { balancesAPI } from '../services/balances';
import { absencesAPI } from '../services/absences';
import { parseApiTimestamp } from '../../../utils/formatters/dateFormatter';

import PageHeader from '../../../components/common/PageHeader';
import LockClockIcon from '@mui/icons-material/LockClock';

// activeStep = index of step currently in progress (0-based).
// closing:    step 0 (Gennemgå) highlighted — review approvals, then click Beregn.
// calculated: steps 0+1 done ✓✓, step 2 (Eksportér) highlighted — review salary, then lock.
// exported:   all 3 steps done ✓✓✓.
function getStateConfig(t) {
  return {
    open:       { label: t('tidsregistering:period_closing.states.open'),       color: 'default', activeStep: 0 },
    closing:    { label: t('tidsregistering:period_closing.states.closing'),    color: 'warning', activeStep: 0 },
    calculated: { label: t('tidsregistering:period_closing.states.calculated'), color: 'info',    activeStep: 2 },
    exported:   { label: t('tidsregistering:period_closing.states.exported'),   color: 'success', activeStep: 3 },
  };
}

function getSteps(t) {
  return [
    {
      label: t('tidsregistering:period_closing.steps.review.label'),
      description: t('tidsregistering:period_closing.steps.review.description'),
    },
    {
      label: t('tidsregistering:period_closing.steps.calculate.label'),
      description: t('tidsregistering:period_closing.steps.calculate.description'),
    },
    {
      label: t('tidsregistering:period_closing.steps.export.label'),
      description: t('tidsregistering:period_closing.steps.export.description'),
    },
  ];
}

function fmt2(n) {
  return Number(n ?? 0).toFixed(2).replace('.', ',');
}

function InitiateDialog({ open, onClose, onSubmit, loading }) {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const lastMonth = subMonths(new Date(), 1);
  const [periodStart, setPeriodStart] = useState(startOfMonth(lastMonth));
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(lastMonth));

  const handleSubmit = () => {
    onSubmit({
      period_start: format(periodStart, 'yyyy-MM-dd'),
      period_end: format(periodEnd, 'yyyy-MM-dd'),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('tidsregistering:period_closing.initiate_dialog.title')}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            {t('tidsregistering:period_closing.initiate_dialog.what_happens_title')}
          </Typography>
          <Typography variant="body2">
            {t('tidsregistering:period_closing.initiate_dialog.what_happens_body')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>{t('tidsregistering:period_closing.initiate_dialog.balances_note_bold')}</strong> {t('tidsregistering:period_closing.initiate_dialog.balances_note_rest')}
          </Typography>
        </Alert>
        <>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <DatePicker
              label={t('tidsregistering:period_closing.initiate_dialog.from')}
              value={periodStart}
              onChange={(d) => d && setPeriodStart(d)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label={t('tidsregistering:period_closing.initiate_dialog.to')}
              value={periodEnd}
              onChange={(d) => d && setPeriodEnd(d)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Box>
        </>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon />}
        >
          {t('tidsregistering:period_closing.initiate_dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PeriodDetailDialog({ open, period, onClose, onCalculate, calculateLoading, onExport, exportLoading, staffMap }) {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const { canExport } = useTidsregistreringClosingPeriodPermissions();
  const { staffClosingPeriods, fetchStaffClosingPeriods, loading: scpLoading } = useClosingPeriods();
  const [dayOverviews, setDayOverviews] = useState([]);
  const [overviewsLoading, setOverviewsLoading] = useState(false);
  const [salaryExports, setSalaryExports] = useState({});
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [pendingWishes, setPendingWishes] = useState([]);

  // Fetch SCPs, day overviews, and pending absence wishes whenever dialog opens for a period
  useEffect(() => {
    if (!open || !period) return;
    fetchStaffClosingPeriods(period.closing_period_id);
    setOverviewsLoading(true);
    dayOverviewsAPI
      .listForDepartment(period.period_start, period.period_end)
      .then((res) => setDayOverviews(res.data?.day_overviews ?? []))
      .catch(() => setDayOverviews([]))
      .finally(() => setOverviewsLoading(false));
    absencesAPI
      .listForDepartment(period.period_start, period.period_end)
      .then((res) => setPendingWishes((res.data?.absences ?? []).filter((a) => a.approval_state === 'approval_wish')))
      .catch(() => setPendingWishes([]));
  }, [open, period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch salary export summaries once balances are calculated (calculated or exported state)
  useEffect(() => {
    if (!open || !['calculated', 'exported'].includes(period?.state) || staffClosingPeriods.length === 0) return;
    setSalaryLoading(true);
    Promise.all(
      staffClosingPeriods.map((scp) =>
        balancesAPI
          .getSalaryExport(scp.staff_member_id, period.period_start, period.period_end)
          .then((res) => ({ id: scp.staff_member_id, data: res.data }))
          .catch(() => ({ id: scp.staff_member_id, data: null }))
      )
    )
      .then((results) => {
        const map = {};
        for (const { id, data } of results) map[id] = data;
        setSalaryExports(map);
      })
      .finally(() => setSalaryLoading(false));
  }, [open, period?.state, staffClosingPeriods.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const overviewsByStaff = useMemo(() => {
    const map = {};
    for (const ov of dayOverviews) {
      if (!map[ov.staff_member_id]) map[ov.staff_member_id] = [];
      map[ov.staff_member_id].push(ov);
    }
    return map;
  }, [dayOverviews]);

  const reviewRows = useMemo(
    () =>
      staffClosingPeriods.map((scp) => {
        const ovs = overviewsByStaff[scp.staff_member_id] ?? [];
        const totalDays = ovs.length;
        const userOk = ovs.filter((o) => o.approved_by_user).length;
        const adminOk = ovs.filter((o) => o.approved_by_admin).length;
        const totalHours = ovs.reduce((s, o) => s + parseFloat(o.hours_sum || 0), 0);
        const allApproved = totalDays > 0 && userOk === totalDays && adminOk === totalDays;
        return { scp, totalDays, userOk, adminOk, totalHours, allApproved };
      }),
    [staffClosingPeriods, overviewsByStaff]
  );

  if (!period) return null;

  const cfg = getStateConfig(t)[period.state] ?? { label: period.state, color: 'default', activeStep: 0 };
  const isClosing = period.state === 'closing';
  const isCalculated = period.state === 'calculated';
  const isExported = period.state === 'exported';
  const loading = scpLoading || overviewsLoading;

  const pendingCount = reviewRows.filter((r) => !r.allApproved).length;
  const totalStaff = reviewRows.length;
  const totalHoursAll = reviewRows.reduce((s, r) => s + r.totalHours, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {format(new Date(period.period_start), 'dd/MM/yyyy')} – {format(new Date(period.period_end), 'dd/MM/yyyy')}
          </Typography>
          <Chip label={cfg.label} color={cfg.color} size="small" />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={cfg.activeStep} sx={{ mb: 3 }}>
          {getSteps(t).map((s) => (
            <Step key={s.label}>
              <StepLabel
                optional={
                  <Typography variant="caption" color="text.secondary">
                    {s.description}
                  </Typography>
                }
              >
                {s.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {period.closed_at && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {t('tidsregistering:period_closing.detail.step1_completed', { date: format(parseApiTimestamp(period.closed_at), 'dd/MM/yyyy HH:mm') })}
          </Typography>
        )}

        {/* ── REVIEW phase (closing) ── */}
        {isClosing && (
          <>
            {pendingCount > 0 && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  {t('tidsregistering:period_closing.detail.pending_warning_title', { pending: pendingCount, total: totalStaff })}
                </Typography>
                <Typography variant="body2">
                  {t('tidsregistering:period_closing.detail.pending_warning_body')}
                </Typography>
              </Alert>
            )}
            {pendingCount === 0 && totalStaff > 0 && (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  {t('tidsregistering:period_closing.detail.all_approved', { total: totalStaff })}
                </Typography>
              </Alert>
            )}

            {pendingWishes.length > 0 && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  {t('tidsregistering:period_closing.detail.pending_wishes', { count: pendingWishes.length })}
                </Typography>
                <Typography variant="body2">
                  {t('tidsregistering:period_closing.detail.pending_wishes_cta')}
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('tidsregistering:period_closing.detail.approval_overview', { total: totalStaff, hours: fmt2(totalHoursAll) })}
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : reviewRows.length === 0 ? (
              <Typography variant="body2" color="text.disabled">{t('tidsregistering:period_closing.detail.no_staff')}</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tidsregistering:period_closing.detail.table.staff')}</TableCell>
                      <TableCell align="center">{t('tidsregistering:period_closing.detail.table.days_in_period')}</TableCell>
                      <TableCell align="center">{t('tidsregistering:period_closing.detail.table.approved_staff')}</TableCell>
                      <TableCell align="center">{t('tidsregistering:period_closing.detail.table.approved_admin')}</TableCell>
                      <TableCell align="right">{t('tidsregistering:period_closing.detail.table.total_hours')}</TableCell>
                      <TableCell align="center">{t('tidsregistering:period_closing.detail.table.pending_wishes')}</TableCell>
                      <TableCell align="center">{t('tidsregistering:period_closing.detail.table.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reviewRows.map(({ scp, totalDays, userOk, adminOk, totalHours, allApproved }) => {
                      const member = staffMap[scp.staff_member_id];
                      const wishCount = pendingWishes.filter((w) => w.staff_member_id === scp.staff_member_id).length;
                      return (
                        <TableRow key={scp.staff_closing_period_id} hover>
                          <TableCell>
                            {member
                              ? <StaffMember staffMember={member} avatarSize={24} gap={1} nameVariant="body2" clickable={false} />
                              : <Typography variant="body2" color="text.disabled">{scp.staff_member_id.slice(0, 8)}…</Typography>
                            }
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{totalDays}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              color={userOk === totalDays ? 'success.main' : 'warning.main'}
                              fontWeight={500}
                            >
                              {userOk} / {totalDays}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              color={adminOk === totalDays ? 'success.main' : 'warning.main'}
                              fontWeight={500}
                            >
                              {adminOk} / {totalDays}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{fmt2(totalHours)} t</Typography>
                          </TableCell>
                          <TableCell align="center">
                            {wishCount > 0
                              ? <Chip label={wishCount} color="warning" size="small" />
                              : <Typography variant="body2" color="text.disabled">—</Typography>
                            }
                          </TableCell>
                          <TableCell align="center">
                            {allApproved ? (
                              <Chip icon={<CheckCircleIcon />} label={t('tidsregistering:period_closing.detail.chip.ready')} color="success" size="small" variant="outlined" />
                            ) : (
                              <Chip icon={<WarningIcon />} label={t('tidsregistering:period_closing.detail.chip.pending')} color="warning" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* ── SALARY REVIEW phase (calculated) ── */}
        {isCalculated && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={500}>
              {t('tidsregistering:period_closing.detail.calculated_info')}
            </Typography>
          </Alert>
        )}

        {/* ── SUMMARY / REPORT phase (exported) ── */}
        {isExported && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={500}>
                {t('tidsregistering:period_closing.detail.exported_info')}
              </Typography>
            </Alert>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Salary table shown for both calculated and exported states */}
        {(isCalculated || isExported) && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              {isCalculated
                ? t('tidsregistering:period_closing.detail.calculated_salary_title', { total: totalStaff })
                : t('tidsregistering:period_closing.detail.exported_salary_title', { total: totalStaff })}
            </Typography>

            {loading || salaryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : reviewRows.length === 0 ? (
              <Typography variant="body2" color="text.disabled">{t('tidsregistering:period_closing.detail.no_data')}</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tidsregistering:period_closing.detail.salary_table.staff')}</TableCell>
                      <TableCell align="right">{t('tidsregistering:period_closing.detail.salary_table.normal_hours')}</TableCell>
                      <TableCell align="right">{t('tidsregistering:period_closing.detail.salary_table.overtime')}</TableCell>
                      <TableCell align="right">{t('tidsregistering:period_closing.detail.salary_table.vacation_days')}</TableCell>
                      <TableCell align="right">{t('tidsregistering:period_closing.detail.salary_table.sick_days')}</TableCell>
                      <TableCell align="right">{t('tidsregistering:period_closing.detail.salary_table.pension_employer')}</TableCell>
                      <TableCell align="center">{t('tidsregistering:period_closing.detail.salary_table.balances')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reviewRows.map(({ scp }) => {
                      const member = staffMap[scp.staff_member_id];
                      const ex = salaryExports[scp.staff_member_id];
                      const overtime = ex
                        ? (
                            parseFloat(ex.merarbejde_voluntary_hours || 0) +
                            parseFloat(ex.merarbejde_forced_hours || 0) +
                            parseFloat(ex.overarbejde_hours || 0)
                          )
                        : null;
                      return (
                        <TableRow key={scp.staff_closing_period_id} hover>
                          <TableCell>
                            {member
                              ? <StaffMember staffMember={member} avatarSize={24} gap={1} nameVariant="body2" clickable={false} />
                              : <Typography variant="body2" color="text.disabled">{scp.staff_member_id.slice(0, 8)}…</Typography>
                            }
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {ex ? `${fmt2(ex.normaltid_hours)} t` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={overtime > 0 ? 'warning.main' : 'text.primary'}>
                              {ex ? `${fmt2(overtime)} t` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {ex ? `${fmt2(ex.ferie_days_used)} d.` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={ex && parseFloat(ex.sick_leave_days_total) > 0 ? 'error.main' : 'text.primary'}>
                              {ex ? `${fmt2(ex.sick_leave_days_total)} d.` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {ex ? `${fmt2(ex.pension_employer_amount)} kr.` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {scp.is_locked ? (
                              <Chip icon={<LockIcon />} label={t('tidsregistering:period_closing.detail.chip.locked')} color="success" size="small" variant="outlined" />
                            ) : (
                              <Chip icon={<PendingIcon />} label={t('tidsregistering:period_closing.detail.chip.pending')} color="warning" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
        {isClosing && (
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('tidsregistering:period_closing.detail.calculate_alert', { total: totalStaff })}
            {pendingCount > 0 && ` ${t('tidsregistering:period_closing.detail.calculate_alert_blocking', { pending: pendingCount })}`}
          </Alert>
        )}
        {isCalculated && (
          <Alert severity="warning" sx={{ width: '100%' }}>
            {t('tidsregistering:period_closing.detail.export_alert')}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose}>{t('common:buttons.close')}</Button>
          {isClosing && canExport && (
            <Button
              variant="contained"
              color="warning"
              size="large"
              startIcon={calculateLoading ? <CircularProgress size={16} color="inherit" /> : <ExportIcon />}
              disabled={calculateLoading || pendingCount > 0}
              onClick={() => onCalculate(period.closing_period_id)}
            >
              {calculateLoading ? t('tidsregistering:period_closing.detail.calculating') : t('tidsregistering:period_closing.detail.calculate_button')}
            </Button>
          )}
          {isCalculated && canExport && (
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
              disabled={exportLoading}
              onClick={() => onExport(period.closing_period_id)}
            >
              {exportLoading ? t('tidsregistering:period_closing.detail.exporting') : t('tidsregistering:period_closing.detail.export_button')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default function PeriodClosingPage() {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { canCreate } = useTidsregistreringClosingPeriodPermissions();
  const { closingPeriods, fetchForDepartment, initiatePeriodClose, calculatePeriod, exportPeriod, loading, error } = useClosingPeriods();
  const { staff, fetchDepartmentStaff } = useStaff();

  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateLoading, setInitiateLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [calculateLoading, setCalculateLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const staffMap = Object.fromEntries((staff ?? []).map((s) => [s.staff_member_id, s]));

  useEffect(() => {
    fetchForDepartment();
    fetchDepartmentStaff();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInitiate = useCallback(async (data) => {
    setInitiateLoading(true);
    const result = await initiatePeriodClose(data);
    setInitiateLoading(false);
    if (result.success) {
      showSuccess(t('tidsregistering:period_closing.success.initiated'));
      setInitiateOpen(false);
      fetchForDepartment();
    } else {
      showError(result.error ?? t('tidsregistering:period_closing.errors.initiate_failed'));
    }
  }, [initiatePeriodClose, showSuccess, showError, fetchForDepartment, t]);

  const handleCalculate = useCallback(async (closingPeriodId) => {
    setCalculateLoading(true);
    const result = await calculatePeriod(closingPeriodId);
    setCalculateLoading(false);
    if (result.success) {
      showSuccess(t('tidsregistering:period_closing.success.calculated'));
      setSelectedPeriod(result.data);
      fetchForDepartment();
    } else {
      showError(result.error ?? t('tidsregistering:period_closing.errors.calculate_failed'));
    }
  }, [calculatePeriod, showSuccess, showError, fetchForDepartment, t]);

  const handleExport = useCallback(async (closingPeriodId) => {
    setExportLoading(true);
    const result = await exportPeriod(closingPeriodId);
    setExportLoading(false);
    if (result.success) {
      showSuccess(t('tidsregistering:period_closing.success.exported'));
      setSelectedPeriod(result.data);
      fetchForDepartment();
    } else {
      showError(result.error ?? t('tidsregistering:period_closing.errors.export_failed'));
    }
  }, [exportPeriod, showSuccess, showError, fetchForDepartment, t]);

  const sorted = [...closingPeriods].sort((a, b) => new Date(b.period_start) - new Date(a.period_start));
  const pendingExportPeriods = sorted.filter((p) => p.state === 'closing' || p.state === 'calculated');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={LockClockIcon}
        title={t('tidsregistering:period_closing.title')}
        subtitle={t('tidsregistering:period_closing.subtitle')}
        actions={canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setInitiateOpen(true)}
          >
            {t('tidsregistering:period_closing.new_period')}
          </Button>
        )}
      />

      {pendingExportPeriods.length > 0 && (
        <Alert
          severity="warning"
          icon={<PendingIcon />}
          action={
            pendingExportPeriods.length === 1 ? (
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                startIcon={<ViewIcon />}
                onClick={() => setSelectedPeriod(pendingExportPeriods[0])}
              >
                {t('tidsregistering:period_closing.review_button')}
              </Button>
            ) : null
          }
        >
          <Typography variant="body2" fontWeight={500}>
            {pendingExportPeriods.length === 1
              ? t('tidsregistering:period_closing.pending_alert.single')
              : t('tidsregistering:period_closing.pending_alert.multiple', { count: pendingExportPeriods.length })}
          </Typography>
          <Typography variant="body2">
            {t('tidsregistering:period_closing.pending_alert.cta')}
          </Typography>
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('tidsregistering:period_closing.table.period')}</TableCell>
              <TableCell align="center">{t('tidsregistering:period_closing.table.status')}</TableCell>
              <TableCell>{t('tidsregistering:period_closing.table.step1_completed')}</TableCell>
              <TableCell align="right">{t('tidsregistering:period_closing.table.action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && closingPeriods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  {t('tidsregistering:period_closing.table.no_periods')}
                </TableCell>
              </TableRow>
            ) : sorted.map((period) => {
              const cfg = getStateConfig(t)[period.state] ?? { label: period.state, color: 'default' };
              const isPending = period.state === 'closing' || period.state === 'calculated';
              return (
                <TableRow
                  key={period.closing_period_id}
                  hover
                  sx={isPending ? { bgcolor: 'warning.50' } : {}}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {format(new Date(period.period_start), 'dd/MM/yyyy')} – {format(new Date(period.period_end), 'dd/MM/yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {period.closed_at ? format(parseApiTimestamp(period.closed_at), 'dd/MM/yyyy HH:mm') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
                      <Tooltip title={isPending ? t('tidsregistering:period_closing.tooltip.review_export') : t('tidsregistering:period_closing.tooltip.view_report')}>
                        <IconButton size="small" onClick={() => setSelectedPeriod(period)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isPending && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<ViewIcon />}
                          onClick={() => setSelectedPeriod(period)}
                        >
                          {t('tidsregistering:period_closing.review_button')}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <InitiateDialog
        open={initiateOpen}
        onClose={() => setInitiateOpen(false)}
        onSubmit={handleInitiate}
        loading={initiateLoading}
      />

      <PeriodDetailDialog
        open={!!selectedPeriod}
        period={selectedPeriod}
        onClose={() => setSelectedPeriod(null)}
        onCalculate={handleCalculate}
        calculateLoading={calculateLoading}
        onExport={handleExport}
        exportLoading={exportLoading}
        staffMap={staffMap}
      />
    </Box>
  );
}
